import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import { styled } from '@mui/material';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';

import { renderCellWithTooltip, renderDate } from '../page-layouts/view/table/ViewTableUtils';
import { LoadingOverlay, NoRowsOverlay, type NoRowsOverlayProps } from '../page-layouts/view/table/ViewTableComponents';
import useSettings from '../hooks/useSettings';
import { alertLocalizedMsg, executeApi } from '../Utils';
import { showConfirmCancelDialog } from '../dialogs/ConfirmCancelDialog';
import ErrorAlert from '../ErrorAlert';
import Icon from '../Icon';

import { deleteFile, getFileExtension, getFileIcon, getFileSize, type MiddlewhereFile } from './FilesUtils';
import FilesExplorerHeader from './components/FilesExplorerHeader';
import FilesExplorerFileDialog from './components/FilesExplorerFileDialog';

interface FilesExplorerTableProps {
	currentPath: string;
	currentSearch?: string;
	refresh: boolean;
	handleClickItem: (file: any) => void;
}

interface FilesExplorerProps {
	folderPath: string;
	disableUpload?: boolean;
	disableDelete?: boolean;
	hidePath?: boolean;
}

declare module '@mui/x-data-grid-premium' {
	interface NoRowsOverlayPropsOverrides extends NoRowsOverlayProps {}
}

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	'& .MuiDataGrid-cell': {
		alignContent: 'center'
	},
	'& .MuiDataGrid-virtualScroller': {
		minHeight: 36
	},
	'& .MuiDataGrid-row:hover': {
		cursor: 'pointer'
	}
}));

function FilesExplorerTable(props: FilesExplorerTableProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const { resetBoundary, showBoundary } = useErrorBoundary();

	const [currentFiles, setCurrentFiles] = React.useState<MiddlewhereFile[]>([]);
	const [isLoadingFiles, setIsLoadingFiles] = React.useState(true);

	const columns: GridColDef[] = [
		{
			field: 'key',
			headerName: '',
			minWidth: 44,
			width: 44,
			renderCell: ({ row }) => <Icon icon={getFileIcon(row)} />,
			sortable: false,
			resizable: false
		},
		{
			field: 'name',
			headerName: t('FILE_EXPLORER_COLUMN_NAME'),
			minWidth: 200,
			flex: 6,
			renderCell: renderCellWithTooltip
		},
		{
			field: 'type',
			headerName: t('FILE_EXPLORER_COLUMN_TYPE'),
			width: 100,
			renderCell: ({ row }) => !row.isDirectory && renderCellWithTooltip({ value: getFileExtension(row.name) }),
			headerAlign: 'right',
			align: 'right'
		},
		{
			field: 'dateModified',
			headerName: t('FILE_EXPLORER_COLUMN_LAST_MODIFIED'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ value }) =>
				renderDate({
					value,
					format: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
				}),
			headerAlign: 'right',
			align: 'right'
		},
		{
			field: 'size',
			type: 'number',
			headerName: t('FILE_EXPLORER_COLUMN_SIZE'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ row }) => renderCellWithTooltip({ value: getFileSize(row) })
		}
	];

	const labelDisplayedRows = ({ from, to, count }) => (
		<FormattedMessage
			id="translationsTableFooter.displayedRows"
			defaultMessage={t('PAGINATOR_ROWS_DISPLAYED')}
			values={{ from, to, total: count }}
		/>
	);

	React.useEffect(() => {
		setIsLoadingFiles(true);
		executeApi.get({
			url: `/api/filexchange/directory?path=${encodeURIComponent(props.currentPath)}`,
			dispatch,
			successHandler: response => {
				if (response.data) setCurrentFiles(response.data || []);
				resetBoundary();
				setIsLoadingFiles(false);
			},
			errorHandler: ex => {
				showBoundary(ex);
				setIsLoadingFiles(false);
			}
		});
	}, [dispatch, props.currentPath, props.refresh, resetBoundary, showBoundary]);

	const filteredFiles = currentFiles.filter(file =>
		file.name.toLowerCase().includes(props.currentSearch?.toLowerCase() || '')
	);

	return (
		<StyledDataGrid
			className="border-0"
			slots={{
				loadingOverlay: LoadingOverlay,
				noRowsOverlay: NoRowsOverlay
			}}
			slotProps={{
				noRowsOverlay: { message: 'FILE_EXPLORER_EMPTY_FOLDER' },
				pagination: { labelDisplayedRows }
			}}
			columns={columns}
			rows={filteredFiles}
			loading={isLoadingFiles}
			getRowId={row => row.key}
			onRowClick={({ row }) => props.handleClickItem(row)}
			density="compact"
			initialState={{
				pagination: { paginationModel: { pageSize: 25 } }
			}}
			pageSizeOptions={[25]}
			disableColumnMenu
			hideFooterSelectedRowCount
		/>
	);
}

export default function FilesExplorer({ folderPath, disableUpload, disableDelete, hidePath }: FilesExplorerProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);

	const cannotDelete = disableDelete || authorizer.cannot('delete', 'sys_documents');

	const [currentPath, setCurrentPath] = React.useState(folderPath);
	const [currentSearch, setCurrentSearch] = React.useState<string>();

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(!refresh);

	const [selectedFile, setSelectedFile] = React.useState<MiddlewhereFile>();
	const [selectedFileContent, setSelectedFileContent] = React.useState();
	const [isLoadingFile, setIsLoadingFile] = React.useState(false);

	const [openSelectedFileDialog, setOpenSelectedFileDialog] = React.useState(false);

	function handleClickItem(file) {
		if (file.isDirectory) {
			setCurrentPath(`${currentPath}\\${file.name}`);
			setSelectedFile(undefined);
		} else {
			setSelectedFile(file);
			setOpenSelectedFileDialog(true);
			setIsLoadingFile(true);

			executeApi.get({
				url: '/api/filexchange/file',
				config: { params: { path: encodeURIComponent(`${currentPath}\\${file.name}`) } },
				dispatch,
				successHandler: response => {
					setSelectedFileContent(response.data);
					setIsLoadingFile(false);
				}
			});
		}
	}

	function handleDeleteFile() {
		dispatch(
			showConfirmCancelDialog({
				title: (
					<FormattedMessage
						id="deleteDialog.title"
						defaultMessage={t('DIALOG_TITLE_DELETE')}
						values={{ title: selectedFile?.name }}
					/>
				),
				onConfirm: () => {
					deleteFile({
						path: `${currentPath}\\${selectedFile?.name}`,
						dispatch,
						successHandler: () => {
							setSelectedFile(undefined);
							setSelectedFileContent(undefined);
							dispatch(alertLocalizedMsg(t, 'ALERT_DELETE_SUCCESS', 'success'));
							updateRefresh();
						}
					});
				}
			})
		);
	}

	return (
		<>
			<FilesExplorerHeader
				folderPath={folderPath}
				currentPath={currentPath}
				setCurrentPath={setCurrentPath}
				currentSearch={currentSearch}
				setCurrentSearch={setCurrentSearch}
				setSelectedFile={setSelectedFile}
				updateRefresh={updateRefresh}
				disableUpload={disableUpload}
				hidePath={hidePath}
			/>
			<ErrorBoundary fallbackRender={({ error }) => <ErrorAlert error={error} />}>
				<FilesExplorerTable
					currentPath={currentPath}
					currentSearch={currentSearch}
					refresh={refresh}
					handleClickItem={handleClickItem}
				/>
			</ErrorBoundary>
			{/* FILE DIALOG */}
			{selectedFile && (
				<FilesExplorerFileDialog
					open={openSelectedFileDialog}
					onClose={() => setOpenSelectedFileDialog(false)}
					basePath={currentPath}
					selectedFile={selectedFile!}
					selectedFileContent={selectedFileContent!}
					isLoadingFile={isLoadingFile}
					handleDeleteFile={handleDeleteFile}
					disableDelete={cannotDelete}
				/>
			)}
		</>
	);
}
